import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";

/* Reusable manual tag editor — attach/detach tags on a contact or
   opportunity via the existing POST /tags/assign and /tags/remove
   endpoints. Self-contained: fetches tag definitions itself and manages
   its own current-tags state seeded from the `tags` prop, so any page can
   drop it in with just entity/recordId/tags — no parent refresh wiring
   required. */
export const TagsEditor = ({ entity, recordId, tags, size = "xs" }) => {
  const [tagDefs, setTagDefs] = useState([]);
  const [current, setCurrent] = useState(tags || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/tags").then((r) => setTagDefs(r.data.tags || [])).catch(() => {}); }, []);
  useEffect(() => { setCurrent(tags || []); }, [recordId]); // eslint-disable-line react-hooks/exhaustive-deps

  const tagDef = (id) => tagDefs.find((t) => t.tag_id === id);

  const addTag = async (tagId) => {
    if (!tagId || current.includes(tagId)) return;
    setBusy(true);
    try {
      await api.post("/tags/assign", { entity, record_id: recordId, tag_id: tagId });
      setCurrent((c) => [...c, tagId]);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const removeTag = async (tagId) => {
    setBusy(true);
    try {
      await api.post("/tags/remove", { entity, record_id: recordId, tag_id: tagId });
      setCurrent((c) => c.filter((t) => t !== tagId));
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const available = tagDefs.filter((t) => !current.includes(t.tag_id));
  const textSize = size === "sm" ? "text-[10px]" : "text-[9px]";

  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`tags-editor-${entity}-${recordId}`}>
      {current.map((id) => {
        const t = tagDef(id);
        return (
          <Badge key={id} variant="outline" className={`${textSize} gap-1 pr-1`}>
            {t ? t.name : id}
            <button type="button" disabled={busy} onClick={() => removeTag(id)}
              data-testid={`tags-editor-remove-${id}`} aria-label={`Remove ${t ? t.name : id} tag`}
              className="ml-0.5 hover:text-destructive disabled:opacity-50">
              <X className="h-2.5 w-2.5" />
            </button>
          </Badge>
        );
      })}
      {available.length > 0 && (
        <Select value="" onValueChange={addTag} disabled={busy}>
          <SelectTrigger data-testid={`tags-editor-add-${entity}-${recordId}`}
            className={`h-6 w-auto ${textSize} border-dashed px-1.5 gap-1`}>
            <SelectValue placeholder="+ Tag" />
          </SelectTrigger>
          <SelectContent>
            {available.map((t) => <SelectItem key={t.tag_id} value={t.tag_id}>{t.name} ({t.group})</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};
